import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import type {
  CaregiverStatusHistoryResponse,
  CaregiverStatusHistoryRow,
} from "./CaregiverProfileStatusReviewSection.types";

export function useCaregiverStatusHistory(caregiverId: CaregiverProfileCaregiver["id"]) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const queryKey = ["caregiverProfileModal", caregiverId, "statusHistory"];
  const statusHistoryQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.get("./caregivers/:caregiverId/status_history", {
        path: { caregiverId },
      }) as Promise<CaregiverStatusHistoryResponse>,
  });
  const deleteFutureStatusMutation = useMutation({
    mutationFn: (row: CaregiverStatusHistoryRow) =>
      api.delete("./caregivers/:caregiverId/deleteFutureStatus/:futureStatusChangeId", {
        path: { caregiverId, futureStatusChangeId: row.id },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["caregiver", "get", caregiverId] });
      toast({ description: "Future status change removed successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not remove future status change.", status: "error" });
    },
  });

  return {
    deleteFutureStatus: deleteFutureStatusMutation.mutate,
    isDeletingFutureStatus: deleteFutureStatusMutation.isPending,
    statusHistoryQuery,
  };
}
